import { create } from 'zustand';
import type { ProcessingStage } from '../types/processing';
import { PROCESSING_STAGES, REFINE_STAGES } from '../data/processing';

/** Unified analysis + refine stage seed (7 + 4 = 11) rendered as one list. */
const COMBINED_STAGES = [...PROCESSING_STAGES, ...REFINE_STAGES];

interface ProcessingState {
  stages: ProcessingStage[];
  feedMessages: { time: string; msg: string; type: string }[];
  counters: { dataPoints: number; insights: number; recommendations: number };
  isComplete: boolean;
  /**
   * Terminal failure flag. Set when the backend emits a non-recoverable
   * ``error`` event (stalled stream, LLM error, agent-loop timeout). Distinct
   * from ``isComplete`` — a failed run must NOT trigger the success-path
   * navigation to /generate-report, it must surface a failed panel so the
   * spinner doesn't run forever.
   */
  isFailed: boolean;
  errorMessage: string | null;
  initPipeline: () => void;
  initRefinePipeline: () => void;
  /**
   * Seed the unified 11-stage pipeline (7 analysis + 4 refine) rendered as ONE
   * continuous progress list for the owner-audit run.
   */
  initCombinedPipeline: () => void;
  advanceStage: (index: number) => void;
  completeStage: (index: number) => void;
  /**
   * Complete a stage WITHOUT bumping the recommendations counter — used for the
   * refine-pass stages, whose completion isn't an analysis "recommendation".
   */
  completeStageNoCounter: (index: number) => void;
  setStageProgress: (index: number, progress: number) => void;
  updateStageSubtask: (index: number, label: string) => void;
  /**
   * Set the known total count of within-stage substeps (e.g. how many
   * audit-result blocks the agent is going to load one-by-one). Used so the
   * UI can render ``Step N of M`` instead of just ``Step N``. Idempotent —
   * once set, only larger values take effect (avoid races where a stale
   * smaller estimate clobbers the real count).
   */
  setStageSubtaskTotal: (index: number, total: number) => void;
  appendFeedMessage: (msg: { time: string; msg: string; type: string }) => void;
  setCounters: (counters: Partial<ProcessingState['counters']>) => void;
  incrementCounters: (delta: Partial<ProcessingState['counters']>) => void;
  markComplete: () => void;
  markFailed: (message: string) => void;
  reset: () => void;
}

// ── RAF-batched feed message buffer ─────────────────────────────────
// Collects messages arriving in the same animation frame and flushes
// them as a single state update, avoiding O(n) array copies per event.
const MAX_FEED_MESSAGES = 200;
let _pendingMessages: { time: string; msg: string; type: string }[] = [];
let _rafId: number | null = null;

function _flushMessages() {
  _rafId = null;
  if (_pendingMessages.length === 0) return;
  const batch = _pendingMessages;
  _pendingMessages = [];
  useProcessingStore.setState((state) => {
    const merged = [...state.feedMessages, ...batch];
    // Cap to avoid unbounded growth
    const trimmed = merged.length > MAX_FEED_MESSAGES
      ? merged.slice(-MAX_FEED_MESSAGES)
      : merged;
    return { feedMessages: trimmed };
  });
}

// ── Throttled stage progress ────────────────────────────────────────
// Stage progress visual changes are barely perceptible at high frequency.
// Throttle to max once per 400ms per stage to reduce re-renders.
const _lastProgressUpdate: Record<number, number> = {};
const PROGRESS_THROTTLE_MS = 400;

export const useProcessingStore = create<ProcessingState>((set) => ({
  stages: PROCESSING_STAGES.map((s) => ({ ...s, progress: 0, status: 'pending' as const })),
  feedMessages: [],
  counters: { dataPoints: 0, insights: 0, recommendations: 0 },
  isComplete: false,
  isFailed: false,
  errorMessage: null,

  initPipeline: () => {
    // Clear RAF buffer to prevent stale messages from prior mount
    _pendingMessages = [];
    if (_rafId) { cancelAnimationFrame(_rafId); _rafId = null; }
    set({
      stages: PROCESSING_STAGES.map((s) => ({ ...s, progress: 0, status: 'pending' as const })),
      feedMessages: [],
      counters: { dataPoints: 0, insights: 0, recommendations: 0 },
      isComplete: false,
      isFailed: false,
      errorMessage: null,
    });
  },

  initRefinePipeline: () => {
    _pendingMessages = [];
    if (_rafId) { cancelAnimationFrame(_rafId); _rafId = null; }
    set({
      stages: REFINE_STAGES.map((s) => ({ ...s, progress: 0, status: 'pending' as const })),
      feedMessages: [],
      counters: { dataPoints: 0, insights: 0, recommendations: 0 },
      isComplete: false,
      isFailed: false,
      errorMessage: null,
    });
  },

  initCombinedPipeline: () => {
    _pendingMessages = [];
    if (_rafId) { cancelAnimationFrame(_rafId); _rafId = null; }
    set({
      stages: COMBINED_STAGES.map((s) => ({ ...s, progress: 0, status: 'pending' as const })),
      feedMessages: [],
      counters: { dataPoints: 0, insights: 0, recommendations: 0 },
      isComplete: false,
      isFailed: false,
      errorMessage: null,
    });
  },

  advanceStage: (index) => {
    // Forward-only guard: if a LATER stage is already active or completed,
    // ignore the event. This protects against StageStarted(1) being
    // re-delivered after we've already moved past it — e.g. on agent-loop
    // recovery (a fresh stage_tracker instance starts at current_stage=0
    // and emits StageStarted(1) on the first marker tool, even though
    // the UI is already on stage 2/3/4). Without this guard the bar
    // jumps back to "stage 1, progress 8%".
    const cur = useProcessingStore.getState().stages;
    const lastNonPending = cur.reduce(
      (acc, s, i) => (s.status !== 'pending' ? i : acc),
      -1,
    );
    if (lastNonPending > index) {
      return;
    }
    // Drop any prior throttle marker for this stage so the very first
    // onToolStart's +15 increment definitely lands (otherwise a leftover
    // timestamp from a previous run could silently throttle it).
    delete _lastProgressUpdate[index];
    set((state) => ({
      stages: state.stages.map((s, i) =>
        i === index
          ? {
              ...s,
              status: 'active' as const,
              subtaskLabel: undefined,
              subtaskCount: 0,
              subtaskTotal: undefined,
              startedAt: Date.now(),
              // Start at a small non-zero value so the bar is immediately
              // visible the moment a stage activates — before any tool
              // start has had time to bump it up. Previously the throttled
              // +15 increments looked like "no progress at all" until at
              // least 2-3 had landed.
              // Only seed 8 when the stage was PENDING. If it's already active
              // (the FE pre-activated "Data Ingestion" on pre-stage tools, then
              // the backend's inferred stage_started re-fires for it ~30s later),
              // keep the accrued progress so the bar never jumps backward.
              progress: s.status === 'pending' ? 8 : s.progress,
            }
          : s
      ),
    }));
  },

  completeStage: (index) =>
    set((state) => {
      // Synthetic recommendations fallback: if the real strategy tools
      // (calculate_health_score / generate_swot / generate_*_report)
      // didn't fire and bump the counter via tallyToolResult, ensure the
      // UI still ticks up by floor(recommendations) = stage_number.
      // We use `max` so real recommendations always dominate the synthetic
      // floor. Stops the counter looking permanently stuck at 0 when a
      // refine pipeline produces files but never the named strategy
      // tools — common in compiled_report-only refine runs.
      const stageNum = index + 1;
      const recsFloor = Math.max(state.counters.recommendations, stageNum);
      return {
        stages: state.stages.map((s, i) =>
          i === index
            ? { ...s, status: 'completed' as const, progress: 100, subtaskLabel: undefined }
            : s
        ),
        counters: { ...state.counters, recommendations: recsFloor },
      };
    }),

  completeStageNoCounter: (index) =>
    set((state) => ({
      stages: state.stages.map((s, i) =>
        i === index
          ? { ...s, status: 'completed' as const, progress: 100, subtaskLabel: undefined }
          : s
      ),
    })),

  updateStageSubtask: (index, label) =>
    set((state) => ({
      stages: state.stages.map((s, i) =>
        i === index
          ? { ...s, subtaskLabel: label, subtaskCount: (s.subtaskCount ?? 0) + 1 }
          : s
      ),
    })),

  setStageSubtaskTotal: (index, total) =>
    set((state) => ({
      stages: state.stages.map((s, i) => {
        if (i !== index) return s;
        // Only accept upward revisions — a later (more authoritative)
        // count may upgrade an early estimate, but never shrink it.
        if (s.subtaskTotal !== undefined && total <= s.subtaskTotal) return s;
        return { ...s, subtaskTotal: total };
      }),
    })),

  setStageProgress: (index, progress) => {
    const now = Date.now();
    const last = _lastProgressUpdate[index] || 0;
    if (now - last < PROGRESS_THROTTLE_MS) return;
    _lastProgressUpdate[index] = now;
    set((state) => ({
      stages: state.stages.map((s, i) =>
        i === index ? { ...s, progress } : s
      ),
    }));
  },

  appendFeedMessage: (msg) => {
    _pendingMessages.push(msg);
    if (!_rafId) {
      _rafId = requestAnimationFrame(_flushMessages);
    }
  },

  setCounters: (counters) =>
    set((state) => ({
      counters: { ...state.counters, ...counters },
    })),

  incrementCounters: (delta) =>
    set((state) => ({
      counters: {
        dataPoints: state.counters.dataPoints + (delta.dataPoints ?? 0),
        insights: state.counters.insights + (delta.insights ?? 0),
        recommendations: state.counters.recommendations + (delta.recommendations ?? 0),
      },
    })),

  markComplete: () => {
    // Flush any pending messages before marking complete
    if (_rafId) {
      cancelAnimationFrame(_rafId);
      _rafId = null;
    }
    _flushMessages();
    // No-op once a run has already failed. This is the single choke point
    // every path that can call markComplete funnels through — the live
    // Centrifugo `done` event, the SSE stream's `onDone` (which also fires
    // on bare transport EOF, with no `done` event at all), and the demo
    // fallback. The backend still yields a `done`/TurnCompleted even from a
    // FAILED run, so without this guard any of those paths flips isComplete
    // back to true and sends the user to an empty /report right after the
    // failed panel already showed.
    set((state) => (state.isFailed ? {} : { isComplete: true }));
  },

  markFailed: (message) => {
    // Flush any pending feed messages so the error line and its context are
    // visible, then set the terminal failed state. Deliberately does NOT set
    // isComplete — the completion effect navigates to /generate-report, which
    // is the success path; a failed run must stay put and show the panel.
    if (_rafId) {
      cancelAnimationFrame(_rafId);
      _rafId = null;
    }
    _flushMessages();
    set({ isFailed: true, errorMessage: message });
  },

  reset: () => {
    // Clear pending buffers
    _pendingMessages = [];
    if (_rafId) {
      cancelAnimationFrame(_rafId);
      _rafId = null;
    }
    Object.keys(_lastProgressUpdate).forEach((k) => delete _lastProgressUpdate[Number(k)]);
    set({
      stages: PROCESSING_STAGES.map((s) => ({ ...s, progress: 0, status: 'pending' as const })),
      feedMessages: [],
      counters: { dataPoints: 0, insights: 0, recommendations: 0 },
      isComplete: false,
      isFailed: false,
      errorMessage: null,
    });
  },
}));
